import React from 'react';
import { FileTextIcon, UserIcon, BuildingIcon, CalendarIcon, ClockIcon, MapPinIcon, DollarSignIcon, TruckIcon, TagIcon } from 'lucide-react';
export function QuotePreview({
  quoteData,
  quoteType,
  quoteNumber
}) {
  // Valores por defecto para evitar errores
  const customerInfo = quoteData.customerInfo || {};
  const shipping = quoteData.shipping || {};
  const currency = shipping.currency || quoteData.currency || '$';
  const pivots = quoteData.pivots || [];
  const parts = quoteData.parts || [];
  const items = quoteData.items || [];
  const issueDate = quoteData.issueDate || quoteData.date || 'Unknown date';
  const expiryDate = quoteData.expiryDate || 'Unknown';
  return <div className="text-gray-800" data-id="element-4867">
      {/* Header */}
      <div className="flex justify-between items-start mb-8 pb-8 border-b border-gray-200" data-id="element-4868">
        <div data-id="element-4869">
          <div className="flex items-center mb-2" data-id="element-4870">
            <div className="p-2 mr-3 bg-blue-600 rounded-lg" data-id="element-4871">
              <FileTextIcon size={20} className="text-white" data-id="element-4872" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800" data-id="element-4873">
              Quote #{quoteNumber}
            </h1>
          </div>
          <div className="text-gray-600" data-id="element-4874">
            {quoteType} Quote • Created by {quoteData.quotedBy || 'Unknown'}
          </div>
          <div className="mt-4 flex flex-wrap gap-4" data-id="element-4875">
            <div className="flex items-center text-gray-600" data-id="element-4876">
              <CalendarIcon size={16} className="mr-1" data-id="element-4877" />
              Issue Date: {issueDate}
            </div>
            <div className="flex items-center text-gray-600" data-id="element-4878">
              <ClockIcon size={16} className="mr-1" data-id="element-4879" />
              Expiry Date: {expiryDate}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end" data-id="element-4880">
          <div className="text-3xl font-bold text-blue-600" data-id="element-4881">
            {currency}
            {(quoteData.amount || 0).toLocaleString(undefined, {
            maximumFractionDigits: 2
          })}
          </div>
          {quoteData.status && <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm" data-id="element-4882">
              <TagIcon size={14} className="mr-1" data-id="element-4883" />
              {quoteData.status.charAt(0).toUpperCase() + quoteData.status.slice(1)}
            </div>}
        </div>
      </div>

      {/* Customer Information */}
      <div className="mb-8" data-id="element-4884">
        <h2 className="text-xl font-bold mb-4" data-id="element-4885">Customer Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-4 rounded-lg" data-id="element-4886">
          <div data-id="element-4887">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4888">Bill To</h3>
            <div className="flex items-start" data-id="element-4889">
              {customerInfo.clientType === 'dealer' || quoteData.clientType === 'dealer' ? <BuildingIcon size={18} className="mr-2 text-gray-400 mt-0.5" data-id="element-4890" /> : <UserIcon size={18} className="mr-2 text-gray-400 mt-0.5" data-id="element-4891" />}
              <div data-id="element-4892">
                <div className="font-medium" data-id="element-4893">
                  {customerInfo.billToCompany || customerInfo.dealerName || quoteData.dealerName || 'Not specified'}
                </div>
                <div className="text-gray-600" data-id="element-4894">
                  {customerInfo.billToAddress || 'No address provided'}
                </div>
                <div className="text-gray-600" data-id="element-4895">
                  {customerInfo.billToCountry || quoteData.country || 'Country not specified'}
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-4896">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4897">Ship To</h3>
            <div className="flex items-start" data-id="element-4898">
              <MapPinIcon size={18} className="mr-2 text-gray-400 mt-0.5" data-id="element-4899" />
              <div data-id="element-4900">
                <div className="font-medium" data-id="element-4901">
                  {customerInfo.shipToCompany || customerInfo.dealerName || quoteData.dealerName || 'Same as billing'}
                </div>
                <div className="text-gray-600" data-id="element-4902">
                  {customerInfo.shipToAddress || 'No address provided'}
                </div>
                <div className="text-gray-600" data-id="element-4903">
                  {customerInfo.shipToCountry || customerInfo.billToCountry || quoteData.country || 'Country not specified'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quote Items */}
      <div className="mb-8" data-id="element-4904">
        <h2 className="text-xl font-bold mb-4" data-id="element-4905">Quote Items</h2>
        <div className="overflow-x-auto" data-id="element-4906">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4907">
            <thead className="bg-gray-50" data-id="element-4908">
              <tr data-id="element-4909">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4910">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4911">
                  Quantity
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4912">
                  Unit Price
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4913">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-4914">
              {/* Render pivot items if available */}
              {pivots.length > 0 && pivots.map((pivot, index) => <tr key={`pivot-${index}`} data-id="element-4915">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4916">
                      <div className="font-medium text-gray-900" data-id="element-4917">
                        {pivot.name}
                      </div>
                      <div className="text-gray-500" data-id="element-4918">{pivot.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4919">
                      1
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4920">
                      {currency}
                      {pivot.price.toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 font-medium" data-id="element-4921">
                      {currency}
                      {pivot.price.toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                  </tr>)}
              {/* Render parts items if available */}
              {parts.length > 0 && parts.map((part, index) => <tr key={`part-${index}`} data-id="element-4922">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4923">
                      <div className="font-medium text-gray-900" data-id="element-4924">
                        {part.name || part.partNumber}
                      </div>
                      <div className="text-gray-500" data-id="element-4925">{part.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4926">
                      {part.quantity || 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4927">
                      {currency}
                      {(part.price || 0).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 font-medium" data-id="element-4928">
                      {currency}
                      {((part.price || 0) * (part.quantity || 1)).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                  </tr>)}
              {/* Render project items if available */}
              {items.length > 0 && items.map((item, index) => <tr key={`item-${index}`} data-id="element-4929">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4930">
                      <div className="font-medium text-gray-900" data-id="element-4931">
                        {item.name}
                      </div>
                      <div className="text-gray-500" data-id="element-4932">{item.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4933">
                      {item.quantity || 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4934">
                      {currency}
                      {(item.price || 0).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 font-medium" data-id="element-4935">
                      {currency}
                      {((item.price || 0) * (item.quantity || 1)).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                  </tr>)}
              {/* If no items are available, show a placeholder */}
              {pivots.length === 0 && parts.length === 0 && items.length === 0 && <tr data-id="element-4936">
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500" data-id="element-4937">
                      No items specified in this quote
                    </td>
                  </tr>}
              {/* Total row */}
              <tr className="bg-gray-50" data-id="element-4938">
                <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-700" data-id="element-4939">
                  Total Amount:
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-blue-600 font-bold" data-id="element-4940">
                  {currency}
                  {(quoteData.amount || 0).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Shipping & Terms */}
      <div className="mb-8" data-id="element-4941">
        <h2 className="text-xl font-bold mb-4" data-id="element-4942">Shipping & Terms</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50 p-4 rounded-lg" data-id="element-4943">
          <div data-id="element-4944">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4945">
              Payment Terms
            </h3>
            <div className="flex items-center" data-id="element-4946">
              <DollarSignIcon size={18} className="mr-2 text-gray-400" data-id="element-4947" />
              <div className="font-medium" data-id="element-4948">
                {shipping.paymentTerms || 'Not specified'}
              </div>
            </div>
          </div>
          <div data-id="element-4949">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4950">Incoterm</h3>
            <div className="flex items-center" data-id="element-4951">
              <TruckIcon size={18} className="mr-2 text-gray-400" data-id="element-4952" />
              <div className="font-medium" data-id="element-4953">
                {shipping.incoterm || 'Not specified'}
              </div>
            </div>
          </div>
          <div data-id="element-4954">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4955">Currency</h3>
            <div className="flex items-center" data-id="element-4956">
              <DollarSignIcon size={18} className="mr-2 text-gray-400" data-id="element-4957" />
              <div className="font-medium" data-id="element-4958">{currency}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes & Terms */}
      <div className="mb-8" data-id="element-4959">
        <h2 className="text-xl font-bold mb-4" data-id="element-4960">Notes & Terms</h2>
        <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4961">
          <p className="text-gray-600" data-id="element-4962">
            {shipping.notes || quoteData.notes || 'This quote is valid until the expiry date mentioned above. Please contact us if you have any questions.'}
          </p>
        </div>
      </div>
    </div>;
}